import { Injectable } from '@angular/core';
import { CatalogMode, ICatalogDraftState, ICatalogRow } from '../models/catalog';
import { mapCatalogPriceToString, mapCatalogWeightToString, rowHasCatalogData } from '../utils/catalogo-produtos.utils';

type CatalogDraftPersistInput = Omit<ICatalogDraftState, 'companyId'> & { companyId: unknown };

export interface ICatalogDraftRestoreState {
  mode: CatalogMode | null;
  companyId: string | null;
  searchProductName: string;
  manualRows: ICatalogRow[];
  importedRows: ICatalogRow[];
  paginaSelecionada: number;
  tamanhoPagina: number | null;
  isCatalogLoadedFromServer: boolean;
  loadedCatalogCompanyId: string | null;
  catalogLoadMessage: string;
}

@Injectable({
  providedIn: 'root'
})
export class CatalogoProdutosStateService {
  removeDraftState(storageKey: string): void {
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // sem op em erros de storage
    }
  }

  persistDraftState(storageKey: string, state: CatalogDraftPersistInput): void {
    const normalizedState: ICatalogDraftState = {
      ...state,
      companyId: this.normalizeString(state.companyId)
    };

    try {
      sessionStorage.setItem(storageKey, JSON.stringify(normalizedState));
    } catch {
      // sem op em erros de storage
    }
  }

  restoreDraftState(storageKey: string): ICatalogDraftRestoreState | null {
    let rawState: string | null = null;
    try {
      rawState = sessionStorage.getItem(storageKey);
    } catch {
      return null;
    }

    if (!rawState) {
      return null;
    }

    try {
      const parsedState = JSON.parse(rawState) as Partial<ICatalogDraftState>;
      const parsedPageSize = Number(parsedState.tamanhoPagina);

      return {
        mode: this.parseMode(parsedState.mode || null),
        companyId: this.normalizeString(parsedState.companyId),
        searchProductName: this.normalizeString(parsedState.searchProductName) || '',
        manualRows: this.normalizeRows(parsedState.manualRows),
        importedRows: this.normalizeRows(parsedState.importedRows),
        paginaSelecionada: this.normalizeNonNegativeNumber(parsedState.paginaSelecionada),
        tamanhoPagina: Number.isFinite(parsedPageSize) && parsedPageSize > 0 ? parsedPageSize : null,
        isCatalogLoadedFromServer: Boolean(parsedState.isCatalogLoadedFromServer),
        loadedCatalogCompanyId: this.normalizeString(parsedState.loadedCatalogCompanyId),
        catalogLoadMessage: this.normalizeString(parsedState.catalogLoadMessage) || ''
      };
    } catch {
      return null;
    }
  }

  private parseMode(mode: string | null | undefined): CatalogMode | null {
    if (mode === 'manual' || mode === 'upload') {
      return mode;
    }
    return null;
  }

  private normalizeRows(value: unknown): ICatalogRow[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((row) => this.normalizeRow(row as Partial<ICatalogRow>)).filter((row) => rowHasCatalogData(row));
  }

  private normalizeRow(row: Partial<ICatalogRow> | null | undefined): ICatalogRow {
    const source = row || {};

    return {
      itemNumber: this.normalizeString(source.itemNumber) || '',
      brandName: this.normalizeString(source.brandName) || '',
      productName: this.normalizeString(source.productName) || '',
      productDescription: this.normalizeString(source.productDescription) || '',
      routineInformation: this.normalizeString(source.routineInformation) || '',
      productPhotoLink: this.normalizeString(source.productPhotoLink) || '',
      productLink: this.normalizeString(source.productLink) || '',
      categories: this.normalizeString(source.categories) || '',
      productAddToCartCode: this.normalizeString(source.productAddToCartCode) || '',
      weight: mapCatalogWeightToString(source.weight),
      price: mapCatalogPriceToString(source.price),
      acne: this.normalizeString(source.acne) || '',
      wrinkles: this.normalizeString(source.wrinkles) || '',
      redness: this.normalizeString(source.redness) || '',
      eyebag: this.normalizeString(source.eyebag) || '',
      darkCircle: this.normalizeString(source.darkCircle) || '',
      oiliness: this.normalizeString(source.oiliness) || '',
      answersByQuestionId: this.normalizeAnswersByQuestionId(source.answersByQuestionId)
    };
  }

  private normalizeAnswersByQuestionId(value: unknown): { [questionId: string]: string[] } {
    if (!value || typeof value !== 'object') {
      return {};
    }

    const normalized: { [questionId: string]: string[] } = {};
    Object.entries(value as Record<string, unknown>).forEach(([questionId, answers]) => {
      normalized[questionId] = this.normalizeStringArray(answers);
    });
    return normalized;
  }

  private normalizeStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.map((item) => String(item || '').trim()).filter((item) => item.length > 0);
  }

  private normalizeString(value: unknown): string | null {
    const normalized = String(value || '').trim();
    return normalized ? normalized : null;
  }

  private normalizeNonNegativeNumber(value: unknown): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return 0;
    }
    return Math.floor(parsed);
  }
}
